import { useTranslation } from 'react-i18next';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';

import { Eye, LogoutCurve } from 'iconsax-reactjs';

import IconButton from 'components/@extended/IconButton';
import useAuth from 'hooks/useAuth';
import { isImpersonationSession } from 'utils/impersonation';

/** Height of the strip; the main layout reserves the same space under the fixed header. */
export const IMPERSONATION_STRIP_HEIGHT = 32;

// Shown only inside an impersonation tab (tab-scoped sessionStorage session).
// Drawn as a slim, single-line strip at the top of the fixed AppBar so it moves
// with the header instead of fighting it for z-index, and offers a one-click
// exit that ends the impersonated session and closes the tab.
export default function ImpersonationBanner() {
  const { t } = useTranslation();
  const { user, logout } = useAuth() as any;
  const downSm = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  if (!isImpersonationSession()) return null;

  const handleExit = async () => {
    try {
      await logout();
    } finally {
      // The tab was opened by script, so it may close itself; otherwise land on login.
      window.close();
      window.location.assign('/auth/login');
    }
  };

  const exitLabel = t('users.exitImpersonation', { defaultValue: 'Exit' });

  return (
    <Box
      data-testid="impersonation-strip"
      sx={{
        height: IMPERSONATION_STRIP_HEIGHT,
        bgcolor: 'warning.main',
        color: 'warning.contrastText',
        px: { xs: 1.5, sm: 2.5, md: 4.5, lg: 8 }
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ height: 1, minWidth: 0 }}>
        <Eye size={16} variant="Bold" style={{ flexShrink: 0 }} />
        <Typography variant="subtitle2" noWrap sx={{ flex: 1, minWidth: 0, lineHeight: 1 }}>
          {t('users.impersonatingBanner', {
            defaultValue: 'Acting as {{name}}',
            name: user?.name || t('users.impersonateTarget', { defaultValue: 'another user' })
          })}
        </Typography>
        {downSm ? (
          <Tooltip title={exitLabel}>
            <IconButton aria-label={exitLabel} size="small" onClick={handleExit} sx={{ color: 'inherit', flexShrink: 0 }}>
              <LogoutCurve size={18} />
            </IconButton>
          </Tooltip>
        ) : (
          <Button
            size="small"
            variant="text"
            startIcon={<LogoutCurve size={16} />}
            onClick={handleExit}
            sx={{ color: 'inherit', flexShrink: 0, py: 0, minHeight: 0, '&:hover': { bgcolor: 'rgba(255,255,255,0.16)' } }}
          >
            {exitLabel}
          </Button>
        )}
      </Stack>
    </Box>
  );
}
